/**
 * Results.
 *
 * Round by round, because the interesting question after a match is never the
 * total — it is which push was the one that cost you. So every row shows the
 * rung reached, the decision taken and what it paid, side by side with theirs.
 */

import { useState } from 'react';
import { TIER_CONFIG } from '@geogame/shared';
import { byCca3 } from '@geogame/data';

import { points, roundLine, signed, tierLabel } from '../lib/format';
import { emojiGrid, shareOrCopy, shareText } from '../lib/share';
import { useGame } from '../store/gameStore';

export function ResultsScreen(): React.JSX.Element {
  const matchEnd = useGame((s) => s.matchEnd);
  const history = useGame((s) => s.history);
  const matchInfo = useGame((s) => s.matchInfo);
  const playerId = useGame((s) => s.playerId);
  const tier = useGame((s) => s.tier);
  const mode = useGame((s) => s.mode);
  const gameMode = useGame((s) => s.gameMode);
  const startMatch = useGame((s) => s.startMatch);
  const inviteToken = useGame((s) => s.inviteToken);
  const leaveMatch = useGame((s) => s.leaveMatch);
  const [shared, setShared] = useState<string | null>(null);

  const depth = TIER_CONFIG[tier].ladderDepth;
  const you = matchEnd?.scores.find((s) => s.playerId === playerId) ?? matchEnd?.scores[0];
  const others = (matchEnd?.scores ?? []).filter((s) => s.playerId !== playerId);
  // THE BEST of the rest, not the first of them. At two seats those are the
  // same score and this reads exactly as it always did; at six, taking the
  // first would have announced "You win" to a player who came fourth, because
  // `scores` is in seat order and seat order means nothing.
  const opp =
    others.length > 0
      ? others.reduce((best, s) => (s.total > best.total ? s : best))
      : (matchEnd?.scores[1] ?? undefined);
  const total = you?.total ?? history[history.length - 1]?.you.total ?? 0;
  const oppTotal = opp?.total ?? history[history.length - 1]?.opp.total ?? 0;
  const won = total > oppTotal;
  const drew = total === oppTotal;
  // Only meaningful above two seats; a duel already says win, lose or level.
  const tableSize = matchEnd?.scores.length ?? 0;
  const place = tableSize > 2 ? others.filter((s) => s.total > total).length + 1 : null;
  const delta = matchEnd?.ratingDelta ?? null;

  const onShare = (): void => {
    const text = shareText({
      mode: mode ?? 'quick',
      tier,
      total,
      opponentTotal: oppTotal,
      history,
      ladderDepth: depth,
      host: window.location.host,
    });
    void shareOrCopy(text).then((r) =>
      setShared(r === 'failed' ? 'Could not share' : r === 'copied' ? 'Copied' : 'Shared'),
    );
  };

  return (
    <main className="gg-screen gg-scroll">
      <header className="gg-hero">
        <p className="gg-subtitle">
          {tierLabel(tier)} · {history.length} rounds
        </p>
        <h1 className="gg-title" data-outcome={drew ? 'draw' : won ? 'win' : 'loss'}>
          {drew ? 'Level' : won ? 'You win' : 'You lose'}
        </h1>
        <p className="gg-score-line tabular-nums">
          {points(total)} <span className="opacity-50">vs</span> {points(oppTotal)}
        </p>
        {place !== null ? (
          <p className="gg-note tabular-nums">
            {place === 1 ? '1st' : place === 2 ? '2nd' : place === 3 ? '3rd' : `${place}th`} of{' '}
            {tableSize}
          </p>
        ) : null}
        {delta ? (
          <p className="gg-note tabular-nums">
            Rating {Math.round(delta.before)} → {Math.round(delta.after)} (
            {signed(Math.round(delta.after - delta.before))})
          </p>
        ) : null}
        <p className="gg-grid-line" aria-label="Round outcomes">
          {emojiGrid(history, depth)}
        </p>
      </header>

      <section className="gg-section">
        <h2 className="gg-section-title">Round by round</h2>
        <ol className="gg-rounds">
          {history.map((r) => {
            const country = byCca3(r.you.targetCca3);
            return (
              <li key={r.roundIdx} className="gg-round-row">
                <span className="gg-round-idx tabular-nums">{r.roundIdx + 1}</span>
                <span className="gg-round-flag" aria-hidden="true">
                  {country?.flagEmoji ?? '🌍'}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="gg-round-name truncate">
                    {r.targetLabel ?? country?.name ?? r.you.targetCca3}
                  </span>
                  <span className="gg-round-meta">
                    {roundLine(r.you.outcome, r.you.finalRung, depth)}
                  </span>
                </span>
                <span
                  className="gg-round-pot tabular-nums"
                  data-banked={r.you.banked ? 'true' : 'false'}
                >
                  {r.you.banked ? `+${points(r.you.pot)}` : '0'}
                </span>
                <span className="gg-round-pot gg-round-opp tabular-nums">
                  {r.opp.banked ? `+${points(r.opp.pot)}` : '0'}
                </span>
              </li>
            );
          })}
        </ol>
        <p className="gg-note">
          Right column is {matchInfo?.opponent.handle ?? 'your opponent'}.
        </p>
      </section>

      <section className="gg-section gg-actions">
        <button type="button" className="gg-btn gg-btn-primary gg-btn-xl" onClick={onShare}>
          {shared ?? 'Share result'}
        </button>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            className="gg-btn"
            onClick={() => {
              /*
               * A REMATCH GOES BACK TO THE LOBBY, NOT TO THE QUEUE.
               *
               * This button used to call `startMatch({ mode: 'friend' })` with
               * no share token after a duel, and the gateway refuses that
               * outright — so "play again" was broken for exactly the matches
               * where playing again is most obvious: the ones you set up with
               * somebody. Re-joining on the original link puts everyone who
               * presses it back in the same lobby, and the seed is derived
               * afresh so it is a new game rather than the same seven rounds.
               */
              if (inviteToken !== null) {
                startMatch({ mode: 'friend', tier, gameMode, shareToken: inviteToken });
                return;
              }
              startMatch({
                mode: mode === 'daily' ? 'quick' : (mode ?? 'quick'),
                tier,
                gameMode,
              });
            }}
          >
            {inviteToken !== null ? 'Rematch' : 'Play again'}
          </button>
          <button type="button" className="gg-btn" onClick={leaveMatch}>
            Home
          </button>
        </div>
      </section>
    </main>
  );
}
